/**
 * This utility is used for working with Url:s.
 */
export default class UrlUtility {
	/**
	 * Can handle links without protocol and with wrong order of ancor and query parameters.
	 * This property of the link will be kept - if original link had protocol the returned link will too, etc.
	 *
	 * @param link
	 * @param paramToAdd Name of the query search parameter to add to link.
	 * @param paramValueToAdd Value of the query search parameter to add to link.
	 * @returns The link with the query parameter added.
	 */
	public static addQueryParameter(
		link: string | null,
		paramToAdd: string,
		paramValueToAdd: string
	): string | null {
		if (!link || !paramToAdd || !paramValueToAdd) {
			return link;
		}

		const hasProtocol = !!link.match('https?://');
		const url = new URL(hasProtocol ? link : 'http://' + link);
		const hasPath = !!url.pathname && url.pathname !== '/';

		if (url.hash.includes('?')) {
			this.orderHashAndParams(url);
			this.addParamToUrl(url, paramToAdd, paramValueToAdd);

			return this.formattedLink(url, hasProtocol, false, hasPath);
		}

		this.addParamToUrl(url, paramToAdd, paramValueToAdd);

		return this.formattedLink(url, hasProtocol, true, hasPath);
	}

	/**
	 * Adds the supplied parameters to the url.
	 *
	 * @param url
	 * @param paramToAdd
	 * @param paramValueToAdd
	 */
	private static addParamToUrl(url: URL, paramToAdd: string, paramValueToAdd: string): void {
		const params = new URLSearchParams(url.search);
		params.append(paramToAdd, paramValueToAdd);
		url.search = '?' + params.toString();
	}

	/**
	 * @param url An url following the url standard in order.
	 * @param hadProtocolBefore The returned link will contain protocol if it used to have it.
	 * @param hadHashAndParamsInCorrectOrder The returned link will have params in the same order it used to have.
	 * @param hadPathBefore
	 */
	private static formattedLink(
		url: URL,
		hadProtocolBefore: boolean,
		hadHashAndParamsInCorrectOrder: boolean,
		hadPathBefore: boolean
	): string {
		const protocolIfHadProtocolBefore =
			url.protocol && hadProtocolBefore ? url.protocol + '//' : '';
		const portIfHadPortBefore = url.port ? ':' + url.port : '';
		const pathIfHadPathBefore = hadPathBefore
			? url.pathname && url.pathname.charAt(-1) === '/'
				? url.pathname.slice(0, -1)
				: url.pathname
			: '';

		const hashAndParams = hadHashAndParamsInCorrectOrder
			? url.search + url.hash
			: url.hash + url.search;

		return (
			protocolIfHadProtocolBefore +
			url.hostname +
			portIfHadPortBefore +
			pathIfHadPathBefore +
			hashAndParams
		);
	}

	/**
	 * Reorders the supplied url to follow the standard url format.
	 *
	 * @param urlUnordered Url with the hash before the query parametes.
	 */
	private static orderHashAndParams(urlUnordered: URL): void {
		const params = new URLSearchParams();

		const [hashWithoutParams, paramsFromHash] = urlUnordered.hash.split('?');

		urlUnordered.hash = hashWithoutParams;

		const paramsList = paramsFromHash.split('&');
		paramsList.forEach((p) => {
			const [name, value] = p.split('=');
			params.append(name, value);
		});
		urlUnordered.search = '?' + params.toString();
	}
}
